import { identityRecipients, keyLines, shortened } from "../../common/age-identity";
import type { AgeKeypair } from "./types";

// One key written down twice: the secret half as the identity `age -i` reads, and the public half as the recipient
// `age -r` seals to. Both come from typage, age's own TypeScript, which is also what `/cryptography` reads them with;
// it is imported dynamically for the reason `openpgp` is, ML-KEM being the largest thing this page can reach.
export async function generateAgeIdentity(postQuantum: boolean): Promise<AgeKeypair> {
  const { generateHybridIdentity, generateX25519Identity, identityToRecipient } = await import("age-encryption");
  const identity = postQuantum ? await generateHybridIdentity() : await generateX25519Identity();
  const recipient = await identityToRecipient(identity);
  // Whole seconds, which is the RFC 3339 age itself writes, and never the milliseconds an ISO string carries.
  const created = new Date().toISOString().replace(/\.\d+Z$/, "Z");
  return { recipient, file: `# created: ${created}\n# public key: ${recipient}\n${identity}\n` };
}

// `age-keygen -y`: an identity file in and the recipients file it answers for out, which is one `age1` line for every
// key in it and what `age -R` reads. A file of nothing but comments is a file with no key in it and says so.
export async function ageRecipientsFile(text: string): Promise<string> {
  const recipients = await identityRecipients(text);
  if (recipients.length === 0) throw new Error("There is no identity in that file");
  return `${recipients.join("\n")}\n`;
}

// What a box can say about an identity as it is typed, which is its shape and no more — the recipient it answers for
// is derived asynchronously, and a key whose last character is wrong looks like this one either way.
export function identityProblem(text: string): string {
  const bad = keyLines(text).find((line) => !IDENTITY.test(line));
  return bad === undefined ? "" : `That is not an age identity: ${shortened(bad)}`;
}

// Bech32 over an uppercase human-readable part, which is what typage holds an identity to before it decodes one.
const IDENTITY = /^AGE-SECRET-KEY(-PQ)?-1[0-9A-Z]+$/;
